import React, { useEffect, useState, useCallback } from 'react';
import { Modal } from 'antd'; // Import Modal from antd
import Notification from '../../Notification/Notification';
import Media from '../MediaComponent/MediaComponent';
import FilterContainer from '../Filter/FilterContainer'; // Import FilterContainer
import './DeleteMedia.css';
import { fetchMedia } from '../../../services/mediaService'; // Import the new service

const apiUrl = process.env.REACT_APP_API_URL || '';

// buildPaths is no longer used directly in DeleteMedia, but it's a utility function
// that might be useful elsewhere or was part of a larger refactoring.
// For now, we'll keep it but remove the eslint warning.
// eslint-disable-next-line no-unused-vars
function buildPaths(item) {
  // compute thumbnail and original paths and ensure they are absolute when possible
  const hasFilename = item.filename && item.upload_time;
  const year = hasFilename ? new Date(item.upload_time * 1000).getFullYear() : null;
  const month = hasFilename ? (new Date(item.upload_time * 1000).getMonth() + 1) : null;

  const thumbRelative = item.thumbnailUrl || (hasFilename ? `/uploads/${year}/${month}/thumb-${item.filename}` : item.url || '');
  const originalRelative = hasFilename ? `/uploads/${year}/${month}/${item.filename}` : (item.url || '');

  const normalize = (p) => {
    if (!p) return '';
    if (p.startsWith('http') || p.startsWith('data:')) return p;
    if (p.startsWith('/')) return `${apiUrl}${p}`;
    return p;
  };

  return {
    thumbnailUrl: normalize(thumbRelative),
    originalUrl: normalize(originalRelative),
  };
}

function DeleteMedia({ categories = [] }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const token = localStorage.getItem('token') || '';
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState(3);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState('newest');
  const [filters, setFilters] = useState({}); // State to hold current filters

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const normalized = await fetchMedia(token, sortOrder, filters); // Pass filters to fetchMedia
      setItems(normalized);
      setFilteredData(normalized);
      setCurrentPage(1);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [sortOrder, token, filters]); // Add filters to dependencies

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllVisible() {
    const paginated = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const ids = paginated.map((i) => i.id);
    setSelected(new Set(ids));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  const handleFilter = (newFilters) => {
    setFilters(newFilters); // Update the filters state
    setCurrentPage(1); // Reset to the first page when filters change
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
    // local sort for filteredData (will also re-fetch because sortOrder is a dependency)
    const sorted = [...filteredData].sort((a, b) => {
      if (value === 'newest') return new Date(b.uploadDate) - new Date(a.uploadDate);
      if (value === 'oldest') return new Date(a.uploadDate) - new Date(b.uploadDate);
      if (value === 'name') return (a.name || '').localeCompare(b.name || '');
      if (value === 'category') return ('' + (a.category || '')).localeCompare('' + (b.category || ''));
      return 0;
    });
    setFilteredData(sorted);
    setCurrentPage(1);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setCurrentPage(1);
  };

  const handleColumnsChange = (value) => {
    setColumns(value);
  };

  async function deleteItem(id) {
    Modal.confirm({
      title: 'Confirm Delete',
      content: 'Are you sure you want to delete this media?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await fetch(`${apiUrl}/images/${id}`, {
            method: 'DELETE',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!res.ok) throw new Error('Failed to delete');
          setItems((prev) => prev.filter((i) => i.id !== id));
          setFilteredData((prev) => prev.filter((i) => i.id !== id));
          setSelected((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setSuccess('Media deleted');
        } catch (err) {
          setError(err && err.message ? err.message : 'Delete failed');
        }
      },
    });
  }

  async function deleteSelected() {
    if (selected.size === 0) {
      setError('No items selected');
      return;
    }
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Delete ${selected.size} selected media items?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const ids = Array.from(selected);
        let deleted = 0;
        try {
          for (const id of ids) {
            // eslint-disable-next-line no-await-in-loop
            const res = await fetch(`${apiUrl}/images/${id}`, {
              method: 'DELETE',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) {
              const txt = await res.text().catch(() => '');
              throw new Error(`Failed to delete id ${id}: ${txt || res.statusText}`);
            }
            deleted += 1;
          }
          setItems((prev) => prev.filter((i) => !selected.has(i.id)));
          setFilteredData((prev) => prev.filter((i) => !selected.has(i.id)));
          setSelected(new Set());
          setSuccess(`Deleted ${deleted} item(s)`);
        } catch (err) {
          setError(err && err.message ? err.message : 'Failed deleting selected items');
        }
      },
    });
  }

  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="delete-container">
      <FilterContainer
        onFilter={handleFilter}
        onPageSizeChange={handlePageSizeChange}
        onColumnsChange={handleColumnsChange}
        onSortChange={handleSortChange}
        currentPage={currentPage}
        pageSize={pageSize}
        sortOrder={sortOrder}
        columns={columns}
        totalItems={filteredData.length}
        onPageChange={handlePageChange}
        categories={categories}
      />

      <div className="dm-action-buttons-container"> {/* New container for action buttons */}
        <div className="dm-control-item">
          <button className="dm-btn" onClick={selectAllVisible}>
            Select all
          </button>
        </div>
        <div className="dm-control-item">
          <button className="dm-btn" onClick={clearSelection}>
            Clear
          </button>
        </div>
        <div className="dm-control-item">
          <button
            className={`dm-btn ${selected.size ? 'dm-btn-danger' : ''}`}
            onClick={deleteSelected}
            disabled={selected.size === 0}
          >
            Delete selected ({selected.size})
          </button>
        </div>
        <div className="dm-control-item">
          {loading && <span style={{ marginLeft: 8 }}>Loading...</span>}
        </div>
      </div>

      <div className="dm-grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {paginatedData.length === 0 && !loading && <div>No media found.</div>}

        {paginatedData.map((item, idx) => {
          const indexInFiltered = (currentPage - 1) * pageSize + idx;
          const name = item.originalname || item.name || item.filename || 'unnamed';

          return (
            <div key={item.id} className="dm-item">
              <input
                className="dm-checkbox"
                type="checkbox"
                checked={selected.has(item.id)}
                onChange={() => toggleSelect(item.id)}
                aria-label={`Select ${name}`}
              />

              <Media categories={categories} size={160} currentIndex={indexInFiltered} data={filteredData} />

              <div className="dm-actions">
                <button className="dm-btn dm-btn-danger" onClick={() => deleteItem(item.id)}>
                  Delete
                </button>

              </div>
            </div>
          );
        })}
      </div>

      <Notification
        message={error || success}
        type={error ? 'error' : success ? 'success' : ''}
        onClose={() => {
          setError('');
          setSuccess('');
        }}
      />
    </div>
  );
}

export default DeleteMedia;
